<template>
  <div v-if="visible" class="campaign-details-overlay" @click="handleOverlayClick">
    <div class="campaign-details-modal" @click.stop>
      <!-- Header -->
      <div class="campaign-details-header">
        <div class="campaign-details-title">
          <div class="campaign-details-icon">
            <Volume2 :size="20" />
          </div>
          <h3>{{ getTranslations('expression_861') }}</h3>
        </div>
        <button class="campaign-details-close" @click="closeModal">
          <X :size="18" />
        </button>
      </div>

      <!-- Body -->
      <div class="campaign-details-body">
        <div v-if="campaigns.campaign.status" class="campaign-details-content">
          <!-- Status Alert -->
          <div class="campaign-details-status-container">
            <div 
              :style="`background: ${campaigns.campaign.status.color}`" 
              class="campaign-details-status-alert"
            >
              <span class="campaign-details-status-text">{{ campaigns.campaign.status.message }}</span>
            </div>
          </div>

          <!-- Campaign Info Grid -->
          <div class="campaign-details-grid">
            <!-- Type and Schedule Row -->
            <div class="campaign-details-row">
              <div class="campaign-details-field">
                <label class="campaign-details-label">{{ getTranslations('expression_865') }}</label>
                <div class="campaign-details-value">
                  <div class="campaign-details-icon-text">
                    <FileText v-if="campaigns.campaign.file" :size="16" />
                    <File v-else :size="16" />
                    <span v-if="campaigns.campaign.file">
                      {{ getTranslations('expression_866') }}
                      <a :href="campaigns.campaign.url_file" target="_blank" class="campaign-details-link">
                        ({{ getTranslations('expression_678').toLowerCase() }})
                      </a>
                    </span>
                    <span v-else>{{ getTranslations('expression_264') }}</span>
                  </div>
                </div>
              </div>
              <div class="campaign-details-field">
                <label class="campaign-details-label">{{ getTranslations('expression_260') }}</label>
                <div class="campaign-details-value">
                  <div class="campaign-details-icon-text">
                    <Mail :size="16" />
                    <span>{{ campaigns.campaign.scheduled ? campaigns.campaign.scheduled : '---' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Dates Row -->
            <div class="campaign-details-row">
              <div class="campaign-details-field">
                <label class="campaign-details-label">{{ getTranslations('expression_867') }}</label>
                <div class="campaign-details-value">
                  <div class="campaign-details-icon-text">
                    <Calendar :size="16" />
                    <span>{{ campaigns.campaign.created ? campaigns.campaign.created : '---' }}</span>
                  </div>
                </div>
              </div>
              <div class="campaign-details-field">
                <label class="campaign-details-label">{{ getTranslations('expression_868') }}</label>
                <div class="campaign-details-value">
                  <div class="campaign-details-icon-text">
                    <CalendarCheck :size="16" />
                    <span>{{ campaigns.campaign.finished ? campaigns.campaign.finished : '---' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Channel and User Row -->
            <div class="campaign-details-row">
              <div class="campaign-details-field">
                <label class="campaign-details-label">{{ getTranslations('expression_4') }}</label>
                <div class="campaign-details-value">
                  <div class="campaign-details-icon-text">
                    <MessageCircle v-if="getChannelObject('id', campaigns.campaign.channel_id).type_key === 0" :size="16" />
                    <MessageSquare v-else :size="16" />
                    <span>{{ getChannelObject('id', campaigns.campaign.channel_id).name ? getChannelObject('id', campaigns.campaign.channel_id).name : '---' }}</span>
                  </div>
                </div>
              </div>
              <div class="campaign-details-field">
                <label class="campaign-details-label">{{ getTranslations('expression_869') }}</label>
                <div class="campaign-details-value">
                  <div class="campaign-details-icon-text">
                    <User :size="16" />
                    <span>{{ campaigns.campaign.user_id ? getUserObject('id', campaigns.campaign.user_id).name : '---' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Campaign Type -->
            <div class="campaign-details-row">
              <div class="campaign-details-field">
                <label class="campaign-details-label">{{ getTranslations('expression_283') }}</label>
                <div class="campaign-details-value">
                  <div class="campaign-details-badge">
                    <span>{{ campaigns.campaign.type }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tags Section -->
            <div class="campaign-details-tags-section">
              <label class="campaign-details-label">{{ getTranslations('expression_268') }}</label>
              <div class="campaign-details-tags-container">
                <div v-if="campaigns.campaign.tags_id && campaigns.campaign.tags_id.length > 0" class="campaign-details-tags-wrapper">
                  <VueSlickCarousel v-bind="settings" :arrows="false" :dots="false" class="campaign-details-carousel">
                    <div 
                      v-for="tag_id in campaigns.campaign.tags_id"
                      :key="tag_id"
                      class="campaign-details-tag-item"
                    >
                      <div class="campaign-details-tag">
                        <span class="campaign-details-tag-text">{{ getTagObject('id', tag_id).name }}</span>
                      </div>
                    </div>
                  </VueSlickCarousel>
                </div>
                <div v-else class="campaign-details-no-tags">
                  <Users :size="16" />
                  <span>{{ getTranslations('expression_269') }}</span>
                </div>
              </div>
            </div>

            <!-- Message Content -->
            <div class="campaign-details-message-section">
              <label class="campaign-details-label">{{ getTranslations('expression_261') }}</label>
              <div class="campaign-details-message-content">
                <!-- Simple Text Message -->
                <div v-if="lengthSplitTextHistoryPipe(campaigns.campaign.text) === 1" class="campaign-details-simple-message">
                  {{ campaigns.campaign.text ? campaigns.campaign.text : '---' }}
                </div>
                
                <!-- Rich Message (HSM) -->
                <div v-else class="campaign-details-rich-message">
                  <div class="campaign-details-media-container">
                    <!-- Image Files -->
                    <div v-if="['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(campaigns.campaign.mime_type_file)" 
                         class="campaign-details-media-item">
                      <img 
                        @click.prevent="setMedia(campaigns.campaign.url_file)"
                        :src="campaigns.campaign.url_file"
                        class="campaign-details-image"
                        alt="Campaign media"
                      />
                    </div>
                    
                    <!-- PDF Files -->
                    <div v-else-if="['application/pdf'].includes(campaigns.campaign.mime_type_file)" 
                         class="campaign-details-media-item campaign-details-pdf"
                         @click.prevent="openUrl(campaigns.campaign.url_file)">
                      <FileText :size="32" />
                    </div>
                    
                    <!-- Video Files -->
                    <div v-else-if="['video/mpeg', 'video/mp4'].includes(campaigns.campaign.mime_type_file)" 
                         class="campaign-details-media-item">
                      <video class="campaign-details-video" controls>
                        <source :src="campaigns.campaign.url_file" type="video/mp4">
                      </video>
                    </div>
                    
                    <!-- Audio Files -->
                    <div v-else-if="['audio/mp3', 'audio/mp4', 'audio/ogg', 'audio/x-m4a', 'audio/mpeg', 'video/webm', 'audio/x-aac', 'audio/x-wav'].includes(campaigns.campaign.mime_type_file)" 
                         class="campaign-details-media-item">
                      <audio class="campaign-details-audio" controls controlsList="nodownload">
                        <source :src="campaigns.campaign.url_file" type="audio/mpeg">
                      </audio>
                    </div>
                  </div>

                  <!-- Message Text Content -->
                  <div class="campaign-details-message-text">
                    <div v-if="getSplitTextHistoryPipe(campaigns.campaign.text).header" class="campaign-details-message-header">
                      {{ getSplitTextHistoryPipe(campaigns.campaign.text).header }}
                    </div>
                    <div class="campaign-details-message-body">
                      {{ getSplitTextHistoryPipe(campaigns.campaign.text).body }}
                    </div>
                    
                    <!-- Message Buttons -->
                    <div v-if="getSplitTextHistoryPipe(campaigns.campaign.text).buttons.length > 0" class="campaign-details-buttons">
                      <div 
                        v-for="(btn, index) in getSplitTextHistoryPipe(campaigns.campaign.text).buttons"
                        :key="index"
                        class="campaign-details-button"
                      >
                        <ExternalLink v-if="btn.type === 'URL'" :size="14" />
                        <Phone v-else-if="btn.type === 'PHONE_NUMBER'" :size="14" />
                        <span>{{ btn.text }}</span>
                      </div>
                    </div>
                    
                    <div v-if="getSplitTextHistoryPipe(campaigns.campaign.text).footer" class="campaign-details-message-footer">
                      {{ getSplitTextHistoryPipe(campaigns.campaign.text).footer }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="campaign-details-footer">
        <div class="campaign-details-footer-left">
          <!-- Report Button -->
          <button 
            v-if="activeModule && 
                  campaigns.campaign.status === getTranslations('expression_262') &&
                  $route.name !== 'ReportsCampaignTracking' &&
                  campaigns.campaign.data &&
                  campaigns.campaign.data !== null"
            @click="showReportCampaign(campaigns.campaign)"
            class="campaign-details-btn campaign-details-btn-outline-primary"
          >
            <Activity :size="16" />
            {{ getTranslations('expression_58') }}
          </button>
        </div>

        <div class="campaign-details-footer-right">
          <!-- Resume Button -->
          <button 
            v-if="campaigns.campaign.type_key === 0 && campaigns.campaign.status === 'Pausada'"
            @click="returnCampaign"
            class="campaign-details-btn campaign-details-btn-primary"
          >
            {{ getTranslations('expression_509') }}
          </button>
          
          <!-- Pause Button -->
          <button 
            v-if="campaigns.campaign.type_key === 0 && (campaigns.campaign.status === 'Executando' || campaigns.campaign.status === 'Pendente')"
            @click="pauseCampaign"
            class="campaign-details-btn campaign-details-btn-primary"
          >
            {{ getTranslations('expression_508') }}
          </button>
          
          <!-- Close Button -->
          <button @click="closeModal" class="campaign-details-btn campaign-details-btn-secondary">
            {{ getTranslations('expression_340') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueSlickCarousel from 'vue-slick-carousel';
import 'vue-slick-carousel/dist/vue-slick-carousel.css';
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css';
import { mapGetters, mapState } from "vuex";
import { 
  Volume2, 
  X, 
  FileText, 
  File, 
  Mail, 
  Calendar, 
  CalendarCheck, 
  MessageCircle, 
  MessageSquare, 
  User, 
  Users, 
  ExternalLink, 
  Phone, 
  Activity 
} from 'lucide-vue';

export default {
  name: "ModalViewCampaign",
  components: {
    VueSlickCarousel,
    Volume2,
    X,
    FileText,
    File,
    Mail,
    Calendar,
    CalendarCheck,
    MessageCircle,
    MessageSquare,
    User,
    Users,
    ExternalLink,
    Phone,
    Activity
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ["close"],
  data() {
    return {
      settings: {
        "infinite": false,
        "centerMode": false,
        "variableWidth": true
      },
    }
  },
  computed: {
    ...mapState([]),
    ...mapGetters([]),

    // Validação temporária - Condição para exibir módulo de rastreio da campanha
    activeModule: function () {
      return ['127.0.0.1', 'localhost', 'dev.beecrm.io', 'homolog.beecrm.io'].includes(window.location.hostname)
    },
  },
  methods: {handleOverlayClick() {
    this.closeModal();
  },
  closeModal() {
    this.$emit('close');
  },
  
setMedia(mediaUrl) {
  this.$store.commit('SET_MEDIA_URL', mediaUrl);

  $('#viewMediaModal').modal('show');
},
  pauseCampaign() {
    const isProduction = document.URL.split('/')[0] == 'https:';
    const session_id = isProduction
      ? document.URL.split('/')[2]
      : 'dev.localhost.io'

    this.$swal.fire({
      title: this.getTranslations('expression_719'),
      text: this.getTranslations('expression_313'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4e73df',
      confirmButtonText: this.getTranslations('expression_498'),
      cancelButtonText: this.getTranslations('expression_499')
    }).then((result) => {
      if (result.isConfirmed) {
        api.patch(`https://lemmonbaileys.beecrm.io/api/whatsapp/campaign/stop`, {
          campaign_id: this.campaigns.campaign.id,
          session_id: `${this.campaigns.campaign.channel_id}-${session_id}`
        }).then((response) => {
          this.$store.dispatch("stopCampaign", this.campaigns.campaign)
        })
      }
    })
  },
    returnCampaign() {
      const isProduction = document.URL.split('/')[0] == 'https:';
      const session_id = isProduction
        ? document.URL.split('/')[2]
        : 'dev.localhost.io'

      this.$swal.fire({
        title: this.getTranslations('expression_735'),
        text: this.getTranslations('expression_245'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#4e73df',
        confirmButtonText: this.getTranslations('expression_498'),
        cancelButtonText: this.getTranslations('expression_499')
      }).then((result) => {
        if (result.isConfirmed) {
          api.patch(`https://lemmonbaileys.beecrm.io/api/whatsapp/campaign/resume`, {
            campaign_id: this.campaigns.campaign.id,
            session_id: `${this.campaigns.campaign.channel_id}-${session_id}`
          }).then((response) => {
            this.$store.dispatch("resumeCampaign", this.campaigns.campaign)
          })
        }
      })
    },
    cancelCampaign(){
      this.$swal.fire({
        title: this.getTranslations('expression_871'),
        text: this.getTranslations('expression_311'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        // cancelButtonColor: 'btn-primary',
        confirmButtonText: this.getTranslations('expression_872'),
        cancelButtonText: this.getTranslations('expression_499')
      }).then((result) => {
        if (result.isConfirmed) {
          this.$store.dispatch("deleteCampaign", this.campaigns.campaign)
        }
      })
    },
    showReportCampaign(campaign) {
      // this.$store.commit('SET_CAMPAIGN_TRACKING', campaign)
      $('#showCampaign').modal('hide')
      window.localStorage.setItem("campaign_id", campaign.id)
      this.$router.push({name: 'ReportsCampaignTracking'})
    },
    lengthSplitTextHistoryPipe(payload) {
      return payload ? payload.split("hsm|").length : 0
    },
    getSplitTypeTextHistoryPipe(payload) {
      return payload ? payload.split("hsm|")[0] : null
    },
    getSplitTextHistoryPipe(payload) {
      return payload && payload.split("hsm|").slice(1) !== undefined
        ? JSON.parse(payload.split("hsm|").slice(1))
        : {}
    },
    openUrl(payload) {
      window.open(payload, "_blank");
    },
  }
}

</script>

<style scoped>
/* ===========================================
   OVERLAY & MODAL CONTAINER
   =========================================== */
.campaign-details-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.campaign-details-modal {
  background: white;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ===========================================
   HEADER
   =========================================== */
.campaign-details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.campaign-details-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.campaign-details-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 10px;
  color: white;
}

.campaign-details-title h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

.campaign-details-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.campaign-details-close:hover {
  background: #e5e7eb;
  color: #374151;
  transform: scale(1.05);
}

/* ===========================================
   BODY & CONTENT
   =========================================== */
.campaign-details-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem;
}

.campaign-details-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* ===========================================
   STATUS ALERT
   =========================================== */
.campaign-details-status-container {
  width: 100%;
}

.campaign-details-status-alert {
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 1rem;
}

.campaign-details-status-text {
  color: white;
  font-weight: 500;
  font-size: 0.875rem;
}

/* ===========================================
   GRID LAYOUT
   =========================================== */
.campaign-details-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.campaign-details-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.campaign-details-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.campaign-details-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.campaign-details-value {
  display: flex;
  align-items: center;
}

.campaign-details-icon-text {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.campaign-details-icon-text svg {
  color: #9ca3af;
  flex-shrink: 0;
}

.campaign-details-link {
  color: #3b82f6;
  text-decoration: none;
}

.campaign-details-link:hover {
  text-decoration: underline;
}

/* ===========================================
   BADGE
   =========================================== */
.campaign-details-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  color: #0369a1;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid #bae6fd;
}

/* ===========================================
   TAGS SECTION
   =========================================== */
.campaign-details-tags-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.campaign-details-tags-container {
  width: 100%;
}

.campaign-details-tags-wrapper {
  width: 100%;
}

.campaign-details-carousel {
  width: 100%;
}

.campaign-details-tag-item {
  padding-right: 0.5rem;
}

.campaign-details-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  color: #374151;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #d1d5db;
  white-space: nowrap;
}

.campaign-details-tag-text {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.campaign-details-no-tags {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px dashed #d1d5db;
}

/* ===========================================
   MESSAGE SECTION
   =========================================== */
.campaign-details-message-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.campaign-details-message-content {
  background: #f9fafb;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
}

.campaign-details-simple-message {
  color: #374151;
  line-height: 1.6;
  font-size: 0.875rem;
}

.campaign-details-rich-message {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.campaign-details-media-container {
  display: flex;
  justify-content: flex-start;
}

.campaign-details-media-item {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.campaign-details-image {
  max-width: 250px;
  height: auto;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.campaign-details-image:hover {
  transform: scale(1.02);
}

.campaign-details-pdf {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  background: #f3f4f6;
  cursor: pointer;
  transition: background 0.2s ease;
}

.campaign-details-pdf:hover {
  background: #e5e7eb;
}

.campaign-details-video {
  max-width: 250px;
  height: auto;
  border-radius: 12px;
}

.campaign-details-audio {
  width: 250px;
  border-radius: 8px;
}

.campaign-details-message-text {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.campaign-details-message-header {
  font-weight: 600;
  font-size: 1rem;
  color: #1f2937;
}

.campaign-details-message-body {
  color: #374151;
  line-height: 1.6;
  font-size: 0.875rem;
}

.campaign-details-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.campaign-details-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: default;
  transition: all 0.2s ease;
}

.campaign-details-button:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.campaign-details-message-footer {
  font-style: italic;
  color: #6b7280;
  font-size: 0.8rem;
  margin-top: 0.5rem;
}

/* ===========================================
   FOOTER
   =========================================== */
.campaign-details-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.campaign-details-footer-left,
.campaign-details-footer-right {
  display: flex;
  gap: 0.75rem;
}

/* ===========================================
   BUTTONS
   =========================================== */
.campaign-details-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.campaign-details-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.campaign-details-btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.campaign-details-btn-primary:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
}

.campaign-details-btn-secondary {
  background: #6b7280;
  color: white;
}

.campaign-details-btn-secondary:hover {
  background: #4b5563;
}

.campaign-details-btn-outline-primary {
  background: white;
  color: #3b82f6;
  border-color: #3b82f6;
}

.campaign-details-btn-outline-primary:hover {
  background: #3b82f6;
  color: white;
}

/* ===========================================
   RESPONSIVE DESIGN
   =========================================== */
@media (max-width: 768px) {
  .campaign-details-modal {
    margin: 1rem;
    max-height: calc(100vh - 2rem);
  }
  
  .campaign-details-header,
  .campaign-details-body,
  .campaign-details-footer {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  .campaign-details-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .campaign-details-footer {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .campaign-details-footer-left,
  .campaign-details-footer-right {
    justify-content: center;
  }
  
  .campaign-details-image,
  .campaign-details-video,
  .campaign-details-audio {
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .campaign-details-title h3 {
    font-size: 1.125rem;
  }
  
  .campaign-details-btn {
    padding: 0.625rem 1rem;
    font-size: 0.8rem;
  }
  
  .campaign-details-buttons {
    flex-direction: column;
  }
  
  .campaign-details-button {
    width: 100%;
    justify-content: center;
  }
}

/* ===========================================
   CAROUSEL OVERRIDES
   =========================================== */
.campaign-details-carousel .slick-track {
  display: flex;
  align-items: center;
}

.campaign-details-carousel .slick-slide {
  height: auto;
}
</style>